import React, { useState } from 'react';
import { Calendar, Activity, Briefcase, DollarSign, Heart, Users, GraduationCap, Plane, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import CosmicBackground from '../components/CosmicBackground';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const LifeIntelligenceDashboard = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [lifeData, setLifeData] = useState(null);
  const [expandedDomains, setExpandedDomains] = useState({});

  // Sample birth data (will be replaced with form input)
  const [birthData, setBirthData] = useState({
    full_name: 'Mandeep Singh',
    gender: 'Male',
    date: '1997-05-14',
    time: '12:17:00',
    place_name: 'Dehradun',
    latitude: 30.3255646,
    longitude: 78.0436813,
    timezone: 'Asia/Kolkata'
  });

  const toggleDomain = (domain) => {
    setExpandedDomains(prev => ({
      ...prev,
      [domain]: !prev[domain]
    }));
  };

  const generateLifeIntelligence = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/life-intelligence/generate`, birthData);
      setLifeData(response.data.life_intelligence);
      toast.success('Life Intelligence Generated Successfully! 🎉');
    } catch (error) {
      console.error('Error generating life intelligence:', error);
      toast.error('Failed to generate life intelligence');
    } finally {
      setLoading(false);
    }
  };

  const domains = [
    {
      id: 'health_longevity',
      name: 'Health & Longevity',
      icon: <Activity className="w-6 h-6" />,
      color: 'from-emerald-500 to-green-600',
      bgColor: 'bg-emerald-500/10'
    },
    {
      id: 'career_profession',
      name: 'Career & Profession',
      icon: <Briefcase className="w-6 h-6" />,
      color: 'from-blue-500 to-cyan-600',
      bgColor: 'bg-blue-500/10'
    },
    {
      id: 'wealth_finance',
      name: 'Wealth & Finance',
      icon: <DollarSign className="w-6 h-6" />,
      color: 'from-yellow-500 to-orange-600',
      bgColor: 'bg-yellow-500/10'
    },
    {
      id: 'relationships_marriage',
      name: 'Relationships & Marriage',
      icon: <Heart className="w-6 h-6" />,
      color: 'from-pink-500 to-rose-600',
      bgColor: 'bg-pink-500/10'
    },
    {
      id: 'family_children',
      name: 'Family & Children',
      icon: <Users className="w-6 h-6" />,
      color: 'from-purple-500 to-indigo-600',
      bgColor: 'bg-purple-500/10'
    },
    {
      id: 'education_skills',
      name: 'Education & Skills',
      icon: <GraduationCap className="w-6 h-6" />,
      color: 'from-indigo-500 to-purple-600',
      bgColor: 'bg-indigo-500/10'
    },
    {
      id: 'travel_foreign',
      name: 'Travel & Foreign',
      icon: <Plane className="w-6 h-6" />,
      color: 'from-cyan-500 to-blue-600',
      bgColor: 'bg-cyan-500/10'
    },
    {
      id: 'spirituality_growth',
      name: 'Spirituality & Growth',
      icon: <Sparkles className="w-6 h-6" />,
      color: 'from-violet-500 to-purple-600',
      bgColor: 'bg-violet-500/10'
    }
  ];

  const renderHealthDomain = (data) => {
    if (!data) return null;
    const { dashboard, complete_details } = data;

    return (
      <div className="space-y-4">
        {/* Dashboard Summary */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div className="bg-white/5 p-3 rounded-lg border border-white/10">
            <p className="text-gray-400 text-xs mb-1">Health Score</p>
            <p className="text-white text-2xl font-bold">{dashboard.health_score}%</p>
          </div>
          <div className="bg-white/5 p-3 rounded-lg border border-white/10">
            <p className="text-gray-400 text-xs mb-1">Major Risk</p>
            <p className="text-white text-sm">{dashboard.major_risk}</p>
          </div>
          <div className="bg-white/5 p-3 rounded-lg border border-white/10">
            <p className="text-gray-400 text-xs mb-1">Timeline</p>
            <p className="text-white text-sm">{dashboard.timeline}</p>
          </div>
        </div>

        {/* Vital Organs */}
        <div className="bg-white/5 p-4 rounded-lg border border-white/10">
          <h4 className="text-white font-semibold mb-3">Vital Organs</h4>
          <div className="grid grid-cols-2 gap-2">
            {dashboard.vital_organs.map((organ, idx) => (
              <div key={idx} className="text-gray-300 text-sm">• {organ}</div>
            ))}
          </div>
        </div>

        {/* Disease Report */}
        {complete_details?.disease_report && (
          <div className="bg-white/5 p-4 rounded-lg border border-white/10">
            <h4 className="text-white font-semibold mb-3">Disease Analysis</h4>
            <div className="space-y-2">
              {Object.entries(complete_details.disease_report).map(([disease, info]) => (
                <div key={disease} className="flex justify-between items-center py-2 border-b border-white/5">
                  <span className="text-gray-300">{disease.replace(/_/g, ' ')}</span>
                  <span className={`px-2 py-1 rounded text-xs ${
                    info.risk === 'Low' ? 'bg-green-500/20 text-green-300' :
                    info.risk === 'Moderate' ? 'bg-yellow-500/20 text-yellow-300' :
                    'bg-red-500/20 text-red-300'
                  }`}>
                    {info.risk} Risk
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Organ Vitality */}
        {complete_details?.organ_vitality && (
          <div className="bg-white/5 p-4 rounded-lg border border-white/10">
            <h4 className="text-white font-semibold mb-3">Organ Vitality Scores</h4>
            <div className="space-y-3">
              {Object.entries(complete_details.organ_vitality).map(([organ, score]) => (
                <div key={organ}>
                  <div className="flex justify-between mb-1">
                    <span className="text-gray-300 text-sm">{organ}</span>
                    <span className="text-white text-sm font-semibold">{score}%</span>
                  </div>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-emerald-500 to-green-600"
                      style={{ width: `${score}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Longevity */}
        {complete_details?.longevity && (
          <div className="bg-white/5 p-4 rounded-lg border border-white/10">
            <h4 className="text-white font-semibold mb-3">Longevity Estimate</h4>
            <div className="space-y-2">
              <p className="text-gray-300">
                <span className="text-white font-semibold">{complete_details.longevity.estimated_years}</span> years
              </p>
              <p className="text-gray-400 text-sm">{complete_details.longevity.category}</p>
              <p className="text-gray-400 text-sm">Range: {complete_details.longevity.range}</p>
            </div>
          </div>
        )}

        {/* Surgery Indicators */}
        {complete_details?.surgery_indicators && complete_details.surgery_indicators.length > 0 && (
          <div className="bg-white/5 p-4 rounded-lg border border-white/10">
            <h4 className="text-white font-semibold mb-3">Surgery Indicators</h4>
            {complete_details.surgery_indicators.map((surgery, idx) => (
              <div key={idx} className="mb-3 pb-3 border-b border-white/5 last:border-0">
                <p className="text-white font-medium">{surgery.type}</p>
                <p className="text-gray-400 text-sm">Years: {surgery.years.join(', ')}</p>
                <p className="text-gray-400 text-sm">Organs: {surgery.organs.join(', ')}</p>
                <p className="text-gray-400 text-sm">Severity: {surgery.severity}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderCareerDomain = (data) => {
    if (!data) return null;
    const { dashboard, complete_details } = data;

    return (
      <div className="space-y-4">
        {/* Dashboard Summary */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div className="bg-white/5 p-3 rounded-lg border border-white/10">
            <p className="text-gray-400 text-xs mb-1">Career Score</p>
            <p className="text-white text-2xl font-bold">{dashboard.career_score}%</p>
          </div>
          <div className="bg-white/5 p-3 rounded-lg border border-white/10">
            <p className="text-gray-400 text-xs mb-1">Best Industry</p>
            <p className="text-white text-sm">{dashboard.best_industry}</p>
          </div>
          <div className="bg-white/5 p-3 rounded-lg border border-white/10">
            <p className="text-gray-400 text-xs mb-1">Next Promotion</p>
            <p className="text-white text-sm">{dashboard.next_promotion}</p>
          </div>
        </div>

        {/* Current Phase */}
        <div className="bg-white/5 p-4 rounded-lg border border-white/10">
          <h4 className="text-white font-semibold mb-2">Current Phase</h4>
          <p className="text-gray-300">{dashboard.current_phase}</p>
          <p className="text-gray-400 text-sm mt-2">Salary Growth: {dashboard.salary_growth}</p>
        </div>

        {/* All Suitable Jobs */}
        {complete_details?.all_suitable_jobs && (
          <div className="bg-white/5 p-4 rounded-lg border border-white/10">
            <h4 className="text-white font-semibold mb-3">Suitable Job Roles</h4>
            {Object.entries(complete_details.all_suitable_jobs).map(([category, jobs]) => (
              <div key={category} className="mb-4">
                <h5 className="text-blue-400 font-medium mb-2">{category}</h5>
                <div className="grid grid-cols-2 gap-2">
                  {jobs.map((job, idx) => (
                    <div key={idx} className="text-gray-300 text-sm">• {job}</div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Company Types */}
        {complete_details?.company_type_analysis && (
          <div className="bg-white/5 p-4 rounded-lg border border-white/10">
            <h4 className="text-white font-semibold mb-3">Company Type Analysis</h4>
            <div className="space-y-3">
              {Object.entries(complete_details.company_type_analysis).map(([type, info]) => (
                <div key={type} className="pb-3 border-b border-white/5 last:border-0">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-white font-medium">{type.replace(/_/g, ' ')}</span>
                    <span className="text-blue-400 font-semibold">{info.score}%</span>
                  </div>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden mb-2">
                    <div 
                      className="h-full bg-gradient-to-r from-blue-500 to-cyan-600"
                      style={{ width: `${info.score}%` }}
                    />
                  </div>
                  <p className="text-gray-400 text-xs">Luck: {info.luck}</p>
                  <p className="text-gray-400 text-xs">Timeline: {info.timeline}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Salary Timeline */}
        {complete_details?.salary_timeline && (
          <div className="bg-white/5 p-4 rounded-lg border border-white/10">
            <h4 className="text-white font-semibold mb-3">Salary Growth Timeline</h4>
            <div className="space-y-3">
              {complete_details.salary_timeline.map((item, idx) => (
                <div key={idx} className="flex justify-between items-center py-2 border-b border-white/5 last:border-0">
                  <div>
                    <p className="text-white font-medium">{item.year}</p>
                    <p className="text-gray-400 text-xs">{item.promotion}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-green-400 font-semibold">{item.salary_lpa} LPA</p>
                    <p className="text-gray-400 text-xs">+{item.growth}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderWealthDomain = (data) => {
    if (!data) return null;
    const { dashboard, complete_details } = data;

    return (
      <div className="space-y-4">
        {/* Dashboard Summary */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div className="bg-white/5 p-3 rounded-lg border border-white/10">
            <p className="text-gray-400 text-xs mb-1">Wealth Score</p>
            <p className="text-white text-2xl font-bold">{dashboard.wealth_score}%</p>
          </div>
          <div className="bg-white/5 p-3 rounded-lg border border-white/10">
            <p className="text-gray-400 text-xs mb-1">Current Net Worth</p>
            <p className="text-white text-sm">{dashboard.current_net_worth}</p>
          </div>
          <div className="bg-white/5 p-3 rounded-lg border border-white/10">
            <p className="text-gray-400 text-xs mb-1">Growth Rate</p>
            <p className="text-white text-sm">{dashboard.wealth_growth}</p>
          </div>
        </div>

        {/* Assets Timeline */}
        {complete_details?.assets_timeline && (
          <div className="bg-white/5 p-4 rounded-lg border border-white/10">
            <h4 className="text-white font-semibold mb-3">Assets Timeline</h4>
            {Object.entries(complete_details.assets_timeline).map(([year, assets]) => (
              <div key={year} className="mb-4 pb-4 border-b border-white/5 last:border-0">
                <h5 className="text-yellow-400 font-medium mb-2">Year {year}</h5>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {Object.entries(assets).map(([key, value]) => (
                    <div key={key} className="text-gray-300">
                      <span className="text-gray-400">{key.replace(/_/g, ' ')}:</span> {value}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Income Streams */}
        {complete_details?.income_streams && (
          <div className="bg-white/5 p-4 rounded-lg border border-white/10">
            <h4 className="text-white font-semibold mb-3">Income Streams</h4>
            {complete_details.income_streams.map((stream, idx) => (
              <div key={idx} className="mb-3 pb-3 border-b border-white/5 last:border-0">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-white font-medium">{stream.source}</span>
                  <span className="text-green-400 font-semibold">{stream.amount}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-400">Stability: {stream.stability}</span>
                  <span className="text-gray-400">Growth: {stream.growth}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Wealth Growth Chart */}
        {complete_details?.wealth_growth_chart && (
          <div className="bg-white/5 p-4 rounded-lg border border-white/10">
            <h4 className="text-white font-semibold mb-3">Wealth Growth Projection</h4>
            <div className="space-y-3">
              {complete_details.wealth_growth_chart.map((item, idx) => (
                <div key={idx}>
                  <div className="flex justify-between mb-1">
                    <span className="text-gray-300">{item.year}</span>
                    <span className="text-yellow-400 font-semibold">{item.net_worth}</span>
                  </div>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-yellow-500 to-orange-600"
                      style={{ width: `${Math.min((idx + 1) * 15, 100)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderRelationshipsDomain = (data) => {
    if (!data) return null;
    const { dashboard, complete_details } = data;

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div className="bg-white/5 p-3 rounded-lg border border-white/10">
            <p className="text-gray-400 text-xs mb-1">Relationship Score</p>
            <p className="text-white text-2xl font-bold">{dashboard.relationship_score}%</p>
          </div>
          <div className="bg-white/5 p-3 rounded-lg border border-white/10">
            <p className="text-gray-400 text-xs mb-1">Marriage Year</p>
            <p className="text-white text-sm">{dashboard.marriage_year}</p>
          </div>
          <div className="bg-white/5 p-3 rounded-lg border border-white/10">
            <p className="text-gray-400 text-xs mb-1">Marriage Quality</p>
            <p className="text-white text-sm">{dashboard.marriage_quality}</p>
          </div>
        </div>

        {complete_details?.love_timeline && (
          <div className="bg-white/5 p-4 rounded-lg border border-white/10">
            <h4 className="text-white font-semibold mb-3">Love Timeline</h4>
            {complete_details.love_timeline.map((item, idx) => (
              <div key={idx} className="mb-3 pb-3 border-b border-white/5 last:border-0">
                <div className="flex justify-between items-center">
                  <span className="text-white font-medium">{item.year}</span>
                  <span className={`px-2 py-1 rounded text-xs ${
                    item.intensity === 'Very High' ? 'bg-pink-500/20 text-pink-300' :
                    item.intensity === 'High' ? 'bg-rose-500/20 text-rose-300' :
                    'bg-purple-500/20 text-purple-300'
                  }`}>{item.intensity}</span>
                </div>
                <p className="text-gray-300 text-sm mt-1">{item.phase}</p>
                <p className="text-gray-400 text-xs">{item.activity}</p>
              </div>
            ))}
          </div>
        )}

        {complete_details?.partner_profile && (
          <div className="bg-white/5 p-4 rounded-lg border border-white/10">
            <h4 className="text-white font-semibold mb-3">Partner Profile</h4>
            <div className="space-y-3">
              <div>
                <h5 className="text-pink-400 text-sm mb-2">Physical</h5>
                {Object.entries(complete_details.partner_profile.physical).map(([key, value]) => (
                  <p key={key} className="text-gray-300 text-sm">
                    <span className="text-gray-400">{key}:</span> {value}
                  </p>
                ))}
              </div>
              <div>
                <h5 className="text-pink-400 text-sm mb-2">Personality</h5>
                {Object.entries(complete_details.partner_profile.personality).map(([key, value]) => (
                  <p key={key} className="text-gray-300 text-sm">
                    <span className="text-gray-400">{key}:</span> {value}
                  </p>
                ))}
              </div>
            </div>
          </div>
        )}

        {complete_details?.family_planning && (
          <div className="bg-white/5 p-4 rounded-lg border border-white/10">
            <h4 className="text-white font-semibold mb-3">Children Planning</h4>
            <p className="text-gray-300 mb-2">Total: {complete_details.family_planning.children_count} children</p>
            <div className="space-y-3">
              <div className="bg-white/5 p-3 rounded">
                <p className="text-white font-medium">First Child: {complete_details.family_planning.first_child.year}</p>
                <p className="text-gray-400 text-sm">{complete_details.family_planning.first_child.gender_probability}</p>
              </div>
              <div className="bg-white/5 p-3 rounded">
                <p className="text-white font-medium">Second Child: {complete_details.family_planning.second_child.year}</p>
                <p className="text-gray-400 text-sm">{complete_details.family_planning.second_child.gender_probability}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderFamilyDomain = (data) => {
    if (!data) return null;
    const { dashboard, complete_details } = data;

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div className="bg-white/5 p-3 rounded-lg border border-white/10">
            <p className="text-gray-400 text-xs mb-1">Family Score</p>
            <p className="text-white text-2xl font-bold">{dashboard.family_score}%</p>
          </div>
          <div className="bg-white/5 p-3 rounded-lg border border-white/10">
            <p className="text-gray-400 text-xs mb-1">Harmony</p>
            <p className="text-white text-sm">{dashboard.harmony}</p>
          </div>
          <div className="bg-white/5 p-3 rounded-lg border border-white/10">
            <p className="text-gray-400 text-xs mb-1">Parenting Quality</p>
            <p className="text-white text-sm">{dashboard.parenting_quality}</p>
          </div>
        </div>

        {complete_details?.children_details && (
          <div className="bg-white/5 p-4 rounded-lg border border-white/10">
            <h4 className="text-white font-semibold mb-3">Children Details</h4>
            <div className="space-y-4">
              <div className="bg-white/5 p-3 rounded">
                <h5 className="text-purple-400 font-medium mb-2">First Child ({complete_details.children_details.first_child.birth_year})</h5>
                <p className="text-gray-300 text-sm">Gender: {complete_details.children_details.first_child.gender}</p>
                <p className="text-gray-300 text-sm">Health: {complete_details.children_details.first_child.health.birth_health}</p>
                <p className="text-gray-300 text-sm">Nature: {complete_details.children_details.first_child.personality.nature}</p>
              </div>
              <div className="bg-white/5 p-3 rounded">
                <h5 className="text-purple-400 font-medium mb-2">Second Child ({complete_details.children_details.second_child.birth_year})</h5>
                <p className="text-gray-300 text-sm">Gender: {complete_details.children_details.second_child.gender}</p>
                <p className="text-gray-300 text-sm">Health: {complete_details.children_details.second_child.health.birth_health}</p>
                <p className="text-gray-300 text-sm">Nature: {complete_details.children_details.second_child.personality.nature}</p>
              </div>
            </div>
          </div>
        )}

        {complete_details?.family_harmony && (
          <div className="bg-white/5 p-4 rounded-lg border border-white/10">
            <h4 className="text-white font-semibold mb-3">Family Harmony Score</h4>
            <p className="text-white text-3xl font-bold mb-3">{complete_details.family_harmony.overall_harmony_score}%</p>
            <div className="space-y-2">
              <p className="text-gray-300 text-sm">
                Atmosphere: {complete_details.family_harmony.home_environment.atmosphere}
              </p>
              <p className="text-gray-300 text-sm">
                Communication: {complete_details.family_harmony.home_environment.communication}
              </p>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderEducationDomain = (data) => {
    if (!data) return null;
    const { dashboard, complete_details } = data;

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div className="bg-white/5 p-3 rounded-lg border border-white/10">
            <p className="text-gray-400 text-xs mb-1">Education Score</p>
            <p className="text-white text-2xl font-bold">{dashboard.education_score}%</p>
          </div>
          <div className="bg-white/5 p-3 rounded-lg border border-white/10">
            <p className="text-gray-400 text-xs mb-1">Current Level</p>
            <p className="text-white text-sm">{dashboard.current_level}</p>
          </div>
          <div className="bg-white/5 p-3 rounded-lg border border-white/10">
            <p className="text-gray-400 text-xs mb-1">Foreign Education</p>
            <p className="text-white text-sm">{dashboard.foreign_education}</p>
          </div>
        </div>

        {complete_details?.skill_analysis?.current_skills && (
          <div className="bg-white/5 p-4 rounded-lg border border-white/10">
            <h4 className="text-white font-semibold mb-3">Current Skills</h4>
            <div className="space-y-3">
              {Object.entries(complete_details.skill_analysis.current_skills.technical || {}).map(([skill, info]) => (
                <div key={skill}>
                  <div className="flex justify-between mb-1">
                    <span className="text-gray-300 text-sm">{skill}</span>
                    <span className="text-indigo-400 font-semibold text-sm">{info.score}%</span>
                  </div>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-indigo-500 to-purple-600"
                      style={{ width: `${info.score}%` }}
                    />
                  </div>
                  <p className="text-gray-400 text-xs mt-1">Level: {info.level}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {complete_details?.certifications_timeline && (
          <div className="bg-white/5 p-4 rounded-lg border border-white/10">
            <h4 className="text-white font-semibold mb-3">Certifications Timeline</h4>
            {complete_details.certifications_timeline.map((cert, idx) => (
              <div key={idx} className="flex justify-between items-center py-2 border-b border-white/5 last:border-0">
                <div>
                  <p className="text-white font-medium text-sm">{cert.cert}</p>
                  <p className="text-gray-400 text-xs">{cert.status}</p>
                </div>
                <span className="text-indigo-400 font-semibold">{cert.year}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderTravelDomain = (data) => {
    if (!data) return null;
    const { dashboard, complete_details } = data;

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div className="bg-white/5 p-3 rounded-lg border border-white/10">
            <p className="text-gray-400 text-xs mb-1">Travel Score</p>
            <p className="text-white text-2xl font-bold">{dashboard.travel_score}%</p>
          </div>
          <div className="bg-white/5 p-3 rounded-lg border border-white/10">
            <p className="text-gray-400 text-xs mb-1">Foreign Chance</p>
            <p className="text-white text-sm">{dashboard.foreign_chance}</p>
          </div>
          <div className="bg-white/5 p-3 rounded-lg border border-white/10">
            <p className="text-gray-400 text-xs mb-1">Emigration</p>
            <p className="text-white text-sm">{dashboard.emigration}</p>
          </div>
        </div>

        {complete_details?.country_analysis && (
          <div className="bg-white/5 p-4 rounded-lg border border-white/10">
            <h4 className="text-white font-semibold mb-3">Country Analysis</h4>
            <div className="space-y-3">
              {Object.entries(complete_details.country_analysis).slice(0, 5).map(([country, info]) => (
                <div key={country} className="pb-3 border-b border-white/5 last:border-0">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-white font-medium">{country}</span>
                    <span className="text-cyan-400 font-semibold">{info.score}%</span>
                  </div>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden mb-2">
                    <div 
                      className="h-full bg-gradient-to-r from-cyan-500 to-blue-600"
                      style={{ width: `${info.score}%` }}
                    />
                  </div>
                  <p className="text-gray-400 text-xs">Visa Success: {info.visa_success}</p>
                  <p className="text-gray-400 text-xs">Settlement: {info.settlement_chance}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {complete_details?.travel_timeline && (
          <div className="bg-white/5 p-4 rounded-lg border border-white/10">
            <h4 className="text-white font-semibold mb-3">Travel Timeline</h4>
            {complete_details.travel_timeline.map((item, idx) => (
              <div key={idx} className="flex justify-between items-center py-2 border-b border-white/5 last:border-0">
                <div>
                  <p className="text-white font-medium">{item.year}</p>
                  <p className="text-gray-400 text-xs">{item.type}</p>
                </div>
                <span className="text-cyan-400 text-sm">{item.trips} trips</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderSpiritualityDomain = (data) => {
    if (!data) return null;
    const { dashboard, complete_details } = data;

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div className="bg-white/5 p-3 rounded-lg border border-white/10">
            <p className="text-gray-400 text-xs mb-1">Spirituality Score</p>
            <p className="text-white text-2xl font-bold">{dashboard.spirituality_score}%</p>
          </div>
          <div className="bg-white/5 p-3 rounded-lg border border-white/10">
            <p className="text-gray-400 text-xs mb-1">Current Phase</p>
            <p className="text-white text-sm">{dashboard.current_phase}</p>
          </div>
          <div className="bg-white/5 p-3 rounded-lg border border-white/10">
            <p className="text-gray-400 text-xs mb-1">Awakening</p>
            <p className="text-white text-sm">{dashboard.awakening}</p>
          </div>
        </div>

        {complete_details?.spiritual_journey?.phases && (
          <div className="bg-white/5 p-4 rounded-lg border border-white/10">
            <h4 className="text-white font-semibold mb-3">Spiritual Journey</h4>
            {complete_details.spiritual_journey.phases.map((phase, idx) => (
              <div key={idx} className="mb-3 pb-3 border-b border-white/5 last:border-0">
                <p className="text-white font-medium">{phase.phase}</p>
                <p className="text-violet-400 text-sm">{phase.years} (Age: {phase.age})</p>
                <p className="text-gray-300 text-sm mt-1">{phase.focus}</p>
                <p className="text-gray-400 text-xs">Level: {phase.spiritual_level}</p>
              </div>
            ))}
          </div>
        )}

        {complete_details?.meditation_analysis && (
          <div className="bg-white/5 p-4 rounded-lg border border-white/10">
            <h4 className="text-white font-semibold mb-3">Meditation Benefits</h4>
            <p className="text-violet-400 mb-3">Suitability: {complete_details.meditation_analysis.suitability}</p>
            <div className="space-y-2">
              {Object.entries(complete_details.meditation_analysis.benefits).map(([benefit, value]) => (
                <div key={benefit} className="flex justify-between text-sm">
                  <span className="text-gray-300">{benefit.replace(/_/g, ' ')}</span>
                  <span className="text-gray-400">{value}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderDomainContent = (domainId, data) => {
    switch(domainId) {
      case 'health_longevity':
        return renderHealthDomain(data);
      case 'career_profession':
        return renderCareerDomain(data);
      case 'wealth_finance':
        return renderWealthDomain(data);
      case 'relationships_marriage':
        return renderRelationshipsDomain(data);
      case 'family_children':
        return renderFamilyDomain(data);
      case 'education_skills':
        return renderEducationDomain(data);
      case 'travel_foreign':
        return renderTravelDomain(data);
      case 'spirituality_growth':
        return renderSpiritualityDomain(data);
      default:
        return <div className="text-gray-400">No data available</div>;
    }
  };

  return (
    <CosmicBackground>
      <div className="min-h-screen relative z-10 px-4 py-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Life Intelligence Dashboard</h1>
          <p className="text-gray-400">Complete life blueprint across all domains</p>
        </div>

        {/* Generate Button */}
        {!lifeData && (
          <div className="mb-8 text-center">
            <button
              onClick={generateLifeIntelligence}
              disabled={loading}
              className="px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-semibold hover:scale-105 transition-transform disabled:opacity-50"
            >
              {loading ? 'Generating...' : 'Generate Life Intelligence'}
            </button>
          </div>
        )}

        {/* Tabs */}
        {lifeData && (
          <>
            <div className="flex gap-4 mb-6 overflow-x-auto">
              {['dashboard', 'calendar', 'details'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                    activeTab === tab
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                      : 'bg-white/5 text-gray-400 hover:bg-white/10'
                  }`}
                >
                  {tab === 'dashboard' && <Activity className="w-5 h-5 inline mr-2" />}
                  {tab === 'calendar' && <Calendar className="w-5 h-5 inline mr-2" />}
                  {tab === 'details' && <Sparkles className="w-5 h-5 inline mr-2" />}
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>

            {/* Dashboard Tab Content */}
            {activeTab === 'dashboard' && (
              <div className="space-y-4">
                {domains.map(domain => {
                  const domainData = lifeData[domain.id];
                  const isExpanded = expandedDomains[domain.id];

                  return (
                    <div key={domain.id} className="bg-white/5 backdrop-blur-xl rounded-xl border border-white/10 overflow-hidden">
                      {/* Domain Header */}
                      <button
                        onClick={() => toggleDomain(domain.id)}
                        className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`p-3 rounded-lg bg-gradient-to-r ${domain.color}`}>
                            {domain.icon}
                          </div>
                          <div className="text-left">
                            <h3 className="text-white font-semibold text-lg">{domain.name}</h3>
                            {domainData?.dashboard && (
                              <p className="text-gray-400 text-sm">
                                Score: {Object.values(domainData.dashboard)[0]}
                              </p>
                            )}
                          </div>
                        </div>
                        {isExpanded ? <ChevronUp className="w-6 h-6 text-white" /> : <ChevronDown className="w-6 h-6 text-white" />}
                      </button>

                      {/* Domain Content */}
                      {isExpanded && (
                        <div className="p-4 border-t border-white/10">
                          {renderDomainContent(domain.id, domainData)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Calendar Tab */}
            {activeTab === 'calendar' && (
              <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
                <h3 className="text-white text-xl font-semibold mb-4">Calendar View</h3>
                <p className="text-gray-400">Calendar implementation coming soon...</p>
              </div>
            )}

            {/* Details Tab */}
            {activeTab === 'details' && (
              <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
                <h3 className="text-white text-xl font-semibold mb-4">Full Details</h3>
                <p className="text-gray-400">Comprehensive PDF-ready view coming soon...</p>
              </div>
            )}
          </>
        )}
      </div>
    </CosmicBackground>
  );
};

export default LifeIntelligenceDashboard;
